import { ref } from 'vue';
import { rowTotal, type PreviewRow } from '../components/timesheetGridRows';
import type { GridColumnKey } from './useGridColumns';

// Header sort for the VP-style grid (ADR-0049). Each click on a header steps
// ascending, descending, then back to the default order. Sorting happens inside
// each group, so Vantagepoint rows and draft rows never mix, and it is never
// saved: every page load starts from the default.

export type GridSortKey = GridColumnKey | 'total';
export type GridSortDir = 'asc' | 'desc';

const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function value(row: PreviewRow, key: GridSortKey): string | number {
  return key === 'total' ? rowTotal(row) : row[key];
}

// Default order inside a group: VP rows as VP lists them (Seq); draft rows in
// the order the draft carries them. A ghost row (a staged pick not yet on the
// sheet) always closes its group.
export function defaultOrder(rows: PreviewRow[]): PreviewRow[] {
  return rows.slice().sort((a, b) => (a.isGhost ? 1 : 0) - (b.isGhost ? 1 : 0) || a.order - b.order);
}

export function useGridSort() {
  const sortKey = ref<GridSortKey | null>(null);
  const sortDir = ref<GridSortDir>('asc');

  function cycle(key: GridSortKey): void {
    if (sortKey.value !== key) {
      sortKey.value = key;
      sortDir.value = 'asc';
    } else if (sortDir.value === 'asc') {
      sortDir.value = 'desc';
    } else {
      sortKey.value = null;
      sortDir.value = 'asc';
    }
  }

  function clear(): void {
    sortKey.value = null;
    sortDir.value = 'asc';
  }

  function ariaSort(key: GridSortKey): 'ascending' | 'descending' | 'none' {
    if (sortKey.value !== key) return 'none';
    return sortDir.value === 'asc' ? 'ascending' : 'descending';
  }

  // Ties keep the default order, so a sort never shuffles equal rows.
  function sortGroup(rows: PreviewRow[]): PreviewRow[] {
    const base = defaultOrder(rows);
    const key = sortKey.value;
    if (!key) return base;
    const sign = sortDir.value === 'asc' ? 1 : -1;
    const ghosts = base.filter(r => r.isGhost);
    const sorted = base
      .filter(r => !r.isGhost)
      .map((row, i) => ({ row, i }))
      .sort((a, b) => {
        const x = value(a.row, key);
        const y = value(b.row, key);
        const c = typeof x === 'number' && typeof y === 'number' ? x - y : COLLATOR.compare(String(x), String(y));
        return sign * c || a.i - b.i;
      })
      .map(o => o.row);
    return [...sorted, ...ghosts];
  }

  return { sortKey, sortDir, cycle, clear, ariaSort, sortGroup };
}
